/**
 * VSyncInstrumentPicker — the reusable "Load from a VSync instrument" block.
 *
 * Extracted verbatim from AddGatewayDialog (P6.3 Step 2) so BOTH the
 * Add-a-Thing wizard AND the Thing detail sheet's "Change equipment type"
 * drawer author a Modbus tag set the same way:
 *
 *   • pick an instrument type ({@link INSTRUMENT_HINTS}) → load a *typical*
 *     register-map template ({@link hintToTags}), or
 *   • paste the device's own `config.txt` / send_config → import the EXACT
 *     addresses ({@link parseVsyncConfig}).
 *
 * Either action rebuilds the caller's tag rows via `onRows`. `onInstrumentType`
 * (optional) reports the applied equipment-type id so the reconfigure drawer can
 * record it on the Thing; the create wizard omits it (no-op). The Modbus wire
 * metadata (`p_id`/`p_type`/`reg_count`) rides along inside each `TagRow`.
 */

import { useState } from "react";
import { Button } from "@/lib/ui/button";
import { useToast } from "@/lib/ui/toast";
import { tagInputToRow, type TagRow } from "@/components/iot/ThingTagRows";
import {
  INSTRUMENT_HINTS,
  instrumentHint,
  hintToTags,
  parseVsyncConfig,
} from "@/lib/iot/vsync-instruments";

export interface VSyncInstrumentPickerProps {
  /** Product default poll interval seeded into imported rows (fallback 5s). */
  pollDefault?: number;
  /** Called with freshly-built rows when a template is loaded / a config imported. */
  onRows: (rows: TagRow[]) => void;
  /**
   * Optional — reports the applied equipment-type id: the hint id on template
   * load, "" on a raw config import (which is not tied to a named type).
   */
  onInstrumentType?: (id: string) => void;
}

export function VSyncInstrumentPicker({
  pollDefault = 5,
  onRows,
  onInstrumentType,
}: VSyncInstrumentPickerProps) {
  const toast = useToast();
  // A typical-map template, or an exact import of the device's own config.
  const [instrHint, setInstrHint] = useState<string>("");
  const [configText, setConfigText] = useState("");

  const loadInstrumentHint = () => {
    const hint = instrumentHint(instrHint);
    if (!hint) return;
    const inputs = hintToTags(hint);
    onRows(inputs.map((t, i) => tagInputToRow(t, `hint_${i}_${Date.now().toString(36)}`, pollDefault)));
    onInstrumentType?.(hint.id);
    toast.push(
      "info",
      `${hint.label}: ${inputs.length} tags loaded — verify the register addresses against your unit.`,
    );
  };

  const importVsyncConfig = () => {
    try {
      const machines = parseVsyncConfig(configText);
      const m = machines[0];
      if (!m) {
        toast.push("error", "No machines found in that config.");
        return;
      }
      onRows(m.tags.map((t, i) => tagInputToRow(t, `cfg_${i}_${Date.now().toString(36)}`, pollDefault)));
      onInstrumentType?.("");
      toast.push(
        "ok",
        `Imported ${m.tags.length} tag${m.tags.length === 1 ? "" : "s"} from machine ${m.mId} ` +
          `(cycle counter @${m.cycleCounterRegister ?? "?"}). Name each metric before saving.`,
      );
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't parse that config.");
    }
  };

  return (
    <details className="rounded-md border border-border bg-surface-elevated px-3 py-2">
      <summary className="cursor-pointer text-[11px] font-medium text-on-surface">
        Load from a VSync instrument
      </summary>
      <div className="mt-2 flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <label className="flex flex-1 flex-col gap-1 text-[11px] text-on-surface-muted">
            Instrument type (typical register map — verify per unit)
            <select
              aria-label="VSync instrument type"
              className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-on-surface"
              value={instrHint}
              onChange={(e) => setInstrHint(e.target.value)}
            >
              <option value="">Select…</option>
              {INSTRUMENT_HINTS.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.label}
                </option>
              ))}
            </select>
          </label>
          <Button size="sm" variant="outline" onClick={loadInstrumentHint} disabled={!instrHint}>
            Load template
          </Button>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] text-on-surface-muted" htmlFor="vsync-config">
            …or paste the device’s config.txt / send_config for the exact addresses
          </label>
          <textarea
            id="vsync-config"
            className="h-20 rounded-md border border-border bg-surface px-2 py-1.5 font-mono text-[11px] text-on-surface"
            placeholder='{"DL_MACHINES":[ … ]}'
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={importVsyncConfig}
              disabled={!configText.trim()}
            >
              Import config
            </Button>
          </div>
        </div>
      </div>
    </details>
  );
}
